import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { FileSignature, ShieldCheck, AlertTriangle, Loader2 } from "lucide-react";
import { toast } from "sonner";
import DOMPurify from "dompurify";

/**
 * In-app Field Introducer Agreement signing block.
 *
 * Reuses the existing contract_templates / contract_instances / contract-sign
 * flow. Does NOT show any referral fee amounts, rate cards or pay tables.
 *
 * Flow:
 *   1. Call rpc ensure_introducer_agreement_instance — creates/returns the
 *      caller's contract_instance against the active introducer_agreement.
 *   2. Render template body_html (sanitised) + 11 required acknowledgements
 *      + typed full-name signature.
 *   3. Invoke edge function contract-sign action='sign'.
 *   4. On success, call onSigned so the gate re-checks and unlocks.
 */

interface Props {
  userId: string;
  onSigned: () => void;
}

const ACKNOWLEDGEMENTS: { key: string; label: string }[] = [
  {
    key: "self_employed",
    label:
      "I understand I am a self-employed independent contractor and not an employee, worker, agent, franchisee or representative authorised to bind EvoHome Improvements.",
  },
  {
    key: "evohome_role",
    label:
      "I understand EvoHome Improvements provides consultancy, coordination, project record and customer support services, and does not physically carry out installation, building, repair, maintenance or home improvement works.",
  },
  {
    key: "no_authority",
    label:
      "I agree not to quote, take payment, promise prices, promise finance, promise grants, promise guarantees, waive rights, or tell customers that EvoHome Improvements physically carries out the works.",
  },
  {
    key: "app_only",
    label:
      "I understand all introductions, customer details, consent records, photos, videos, measurements, notes, plans and documents must be submitted through the EvoHome Improvements app, referral link, QR code or approved system.",
  },
  {
    key: "no_pay_for_signups",
    label:
      "I understand I am not paid for knocking doors, registrations, names, leads, app signups or appointments alone. A referral fee is only available where a properly tracked referral results in a qualifying EvoHome Improvements project and EvoHome Improvements confirms the referral fee is payable.",
  },
  {
    key: "active_status",
    label:
      "I understand referral fee eligibility only applies while I remain active, approved and in good standing. If I resign, leave, become inactive, am suspended, removed or terminated, future unpaid referral fee eligibility ends unless EvoHome Improvements agrees otherwise.",
  },
  {
    key: "no_ownership",
    label:
      "I understand customers, registrations, project records and opportunities handled through EvoHome Improvements are EvoHome Improvements-controlled relationships and I do not own the customer.",
  },
  {
    key: "no_bypass",
    label:
      "I agree not to bypass EvoHome Improvements, arrange private work, send customers directly to installers, sell customer details, create side deals or use EvoHome Improvements opportunities for personal or competing business.",
  },
  {
    key: "fee_confidentiality",
    label:
      "I understand referral fee amounts and commercial arrangements are private EvoHome Improvements business information. I must not screenshot, share, forward, publish, advertise, show, discuss or disclose referral fee information to homeowners, customers, installers, competitors, social media users or third parties.",
  },
  {
    key: "data_privacy",
    label:
      "I agree to protect customer privacy, company information and app access. I will not use EvoHome Improvements systems, customer details, project data, photos, documents, screenshots, scripts or confidential information for any other job, business, competitor, side deal or private purpose.",
  },
  {
    key: "customer_conduct",
    label:
      "I agree to act lawfully, honestly and respectfully, and not to pressure, mislead, harass, exploit or intimidate homeowners.",
  },
];

interface InstanceInfo {
  instance_id: string;
  template_id: string;
  version: number;
  status: string;
}

export default function IntroducerAgreementSign({ userId, onSigned }: Props) {
  const [loading, setLoading] = useState(true);
  const [bootError, setBootError] = useState<string | null>(null);
  const [instance, setInstance] = useState<InstanceInfo | null>(null);
  const [templateTitle, setTemplateTitle] = useState<string>("Field Introducer Agreement");
  const [bodyHtml, setBodyHtml] = useState<string>("");
  const [signerName, setSignerName] = useState<string>("");
  const [acks, setAcks] = useState<Record<string, boolean>>({});
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setBootError(null);
      try {
        const { data, error } = await (supabase as any).rpc(
          "ensure_introducer_agreement_instance",
        );
        if (cancelled) return;
        if (error) {
          setBootError(error.message);
          return;
        }
        if (!data?.ok) {
          if (data?.error === "no_active_template") {
            setBootError(
              "The Field Introducer Agreement has not been published yet. The EvoHome Improvements admin team has been notified — please check back shortly.",
            );
          } else if (data?.error === "not_active_introducer") {
            setBootError(
              "Your introducer profile is not yet active. The EvoHome Improvements team will be in touch shortly.",
            );
          } else if (data?.error === "profile_missing_email") {
            setBootError("Your profile is missing an email address. Please contact EvoHome Improvements.");
          } else {
            setBootError(data?.error || "Could not start the signing flow.");
          }
          return;
        }

        const info: InstanceInfo = {
          instance_id: data.instance_id,
          template_id: data.template_id,
          version: data.version,
          status: data.status,
        };
        setInstance(info);

        const [tplRes, profileRes] = await Promise.all([
          supabase
            .from("contract_templates")
            .select("title, body_html")
            .eq("id", info.template_id)
            .maybeSingle(),
          supabase.from("profiles").select("full_name").eq("id", userId).maybeSingle(),
        ]);
        if (cancelled) return;
        if (tplRes.error) {
          setBootError(tplRes.error.message);
          return;
        }
        setTemplateTitle(tplRes.data?.title ?? "Field Introducer Agreement");
        setBodyHtml(tplRes.data?.body_html ?? "");
        if (profileRes.data?.full_name) setSignerName(profileRes.data.full_name);

        // Mark as viewed for audit
        await supabase.functions
          .invoke("contract-sign", {
            body: { action: "view", contract_id: info.instance_id },
          })
          .catch(() => {});
      } catch (err) {
        if (!cancelled) {
          setBootError(err instanceof Error ? err.message : "Unexpected error.");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [userId]);

  const allAcked = ACKNOWLEDGEMENTS.every((a) => acks[a.key]);
  const nameValid = signerName.trim().length >= 2;
  const canSign = allAcked && nameValid && !!instance;

  const handleSign = async () => {
    if (!instance || !canSign) return;
    setSubmitting(true);
    try {
      const { data, error } = await supabase.functions.invoke("contract-sign", {
        body: {
          action: "sign",
          contract_id: instance.instance_id,
          // Signature is typed name; we encode it into a tiny data URI so the
          // contract-sign function records signature_type='drawn' with the
          // typed name visible inside the signed HTML snapshot.
          signature_data: undefined,
          typed_name: signerName.trim(),
        },
      });
      if (error) {
        toast.error(error.message || "We could not record your signature. Please try again.");
        return;
      }
      if ((data as any)?.error) {
        toast.error((data as any).error);
        return;
      }
      toast.success("Thank you — your Field Introducer Agreement is signed.");
      onSigned();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Unexpected error.");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="p-8 text-center text-sm text-muted-foreground flex items-center justify-center gap-2">
          <Loader2 className="w-4 h-4 animate-spin" /> Preparing your agreement…
        </CardContent>
      </Card>
    );
  }

  if (bootError) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <AlertTriangle className="w-4 h-4 text-amber-600" /> Agreement unavailable
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground">{bootError}</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-lg bg-primary/10 text-primary flex items-center justify-center shrink-0">
              <FileSignature className="w-5 h-5" />
            </div>
            <div className="space-y-1">
              <CardTitle>{templateTitle}</CardTitle>
              <CardDescription>
                Please read the agreement, tick every acknowledgement, type your full name, and click
                <span className="font-medium"> Accept &amp; Sign</span> to unlock your Field Introducer tools.
              </CardDescription>
              <Badge variant="outline" className="mt-1 text-xs">Version {instance?.version}</Badge>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div
            className="prose prose-sm max-w-none border rounded-md p-4 bg-muted/30 max-h-[420px] overflow-y-auto"
            // Body comes from admin-managed template, then sanitised here.
            dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(bodyHtml || "<p>(empty)</p>") }}
          />
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <ShieldCheck className="w-4 h-4 text-primary" /> Required acknowledgements
          </CardTitle>
          <CardDescription>All eleven boxes must be ticked.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          {ACKNOWLEDGEMENTS.map((a) => (
            <label key={a.key} className="flex items-start gap-3 cursor-pointer">
              <Checkbox
                checked={!!acks[a.key]}
                onCheckedChange={(v) => setAcks((s) => ({ ...s, [a.key]: v === true }))}
                className="mt-0.5"
              />
              <Label className="font-normal text-sm leading-relaxed cursor-pointer">{a.label}</Label>
            </label>
          ))}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Type your full name to sign</CardTitle>
          <CardDescription>
            By typing your name and clicking Accept &amp; Sign you agree to be electronically bound by this agreement.
            Your IP address, device user agent, the time of signing and a cryptographic hash of the signed document will be recorded.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="signer-name">Full legal name</Label>
            <Input
              id="signer-name"
              value={signerName}
              onChange={(e) => setSignerName(e.target.value)}
              placeholder="e.g. Jane Smith"
              autoComplete="name"
              disabled={submitting}
            />
          </div>

          {!allAcked && (
            <Alert>
              <AlertDescription className="text-xs">
                Please tick all eleven acknowledgements above before signing.
              </AlertDescription>
            </Alert>
          )}

          <div className="flex flex-wrap gap-2">
            <Button onClick={handleSign} disabled={!canSign || submitting} className="gap-1.5">
              {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileSignature className="w-4 h-4" />}
              Accept &amp; Sign
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
